import { MutableRefObject, useRef, useState } from 'react';
import { FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { UploadProps, UploadRef } from './types';
import { Upload } from 'components/upload/Upload';
import { mergeRefs } from 'components/util/ref';

interface UseFormUploadProps<T extends FieldValues, K extends FieldPath<T>>
    extends UseControllerProps<T, K>,
        Omit<UploadProps, 'name'> {
    inputRef?: MutableRefObject<UploadRef | null>;
}

const UseFormUpload = <T extends FieldValues, K extends FieldPath<T>>(
    props: UseFormUploadProps<T, K>,
) => {
    const {
        inputRef,
        defaultValue,
        control,
        name: nameExternal,
        rules,
        onupdatefiles: onUpdateFilesExternal,
        onInitialized: onInitializedExternal,
        ...restUploadProps
    } = props;
    const {
        field: { value, name, onChange: onChangeInternal, onBlur, ref: formRef },
    } = useController({
        name: nameExternal,
        control,
        rules,
        defaultValue,
    });
    const [didInit, setDidInit] = useState(false);
    const defaultFiles = useRef(value as UploadProps['defaultFiles']);
    const mergedRef = mergeRefs([formRef, ...(inputRef ? [inputRef] : [])]);

    const updateFiles: UploadProps['onupdatefiles'] = files => {
        onChangeInternal(files);
        onUpdateFilesExternal?.(files);
    };

    const onUpdateFiles: UploadProps['onupdatefiles'] = files => {
        if (didInit) {
            updateFiles(files);
        }
    };

    const onInitialized: UploadProps['onInitialized'] = files => {
        setDidInit(true);
        updateFiles(files);
        onBlur();
        onInitializedExternal?.(files);
    };

    return (
        <Upload
            ref={mergedRef}
            defaultFiles={defaultFiles.current}
            name={name}
            onupdatefiles={onUpdateFiles}
            onInitialized={onInitialized}
            {...restUploadProps}
        />
    );
};

export { UseFormUpload };
export type { UseFormUploadProps };
